import { filterDefinitions } from 'components/Filters/filterDefinitions'

/**
 * Extracts a state abbreviation or name from a location string.
 * Handles formats like "Los Angeles, CA", "California", "New York", "TX", "Austin, Texas"
 */
const parseStateFromLocation = (locationStr) => {
  if (!locationStr) return []

  // If it contains a comma, try to parse "City, State" format
  if (locationStr.includes(',')) {
    const parts = locationStr.split(',').map(s => s.trim())
    // Last part is likely the state (e.g., "CA" or "California")
    return parts.slice(1).filter(Boolean)
  }

  // No comma — could be just a state name like "California" or "TX"
  return [locationStr.trim()]
}

/**
 * Matches a state string against available filter states.
 * Tries exact name match, exact value (abbreviation) match, and case-insensitive variants.
 */
const matchState = (stateStr, availableStates) => {
  if (!stateStr || !availableStates?.length) return null
  const normalized = stateStr.trim().toLowerCase()

  // Exact match on name or value (case-insensitive)
  const exactMatch = availableStates.find(
    s => s.name?.toLowerCase() === normalized || s.value?.toLowerCase() === normalized
  )
  if (exactMatch) return exactMatch.value || exactMatch.id

  // Partial match — state name starts with the search term (e.g., "New York" starts with "New")
  // Only for longer strings to avoid false matches
  if (normalized.length >= 4) {
    const partialMatch = availableStates.find(
      s => s.name?.toLowerCase().startsWith(normalized) || normalized.startsWith(s.name?.toLowerCase())
    )
    if (partialMatch) return partialMatch.value || partialMatch.id
  }

  return null
}

/**
 * Fuzzy-matches a specialty name against available specialties.
 * Tries exact match first, then partial/contains match.
 */
const matchSpecialty = (name, availableSpecialties) => {
  if (!name || !availableSpecialties?.length) return null
  const normalized = name.trim().toLowerCase()

  // Exact match
  const exact = availableSpecialties.find(s => s.name?.toLowerCase() === normalized)
  if (exact) return exact.id || exact.value

  // Contains match (e.g., "Cardiology" in "Interventional Cardiology")
  const containsMatch = availableSpecialties.find(
    s => s.name?.toLowerCase().includes(normalized) || normalized.includes(s.name?.toLowerCase())
  )
  if (containsMatch) return containsMatch.id || containsMatch.value

  return null
}

/**
 * Maps AI jobListingArguments to URL filter params.
 *
 * @param {Object} jobListingArguments - AI search criteria
 * @param {Object} filterValues - Available filter values from Redux
 * @returns {URLSearchParams}
 */
export const mapJobListingArgumentsToFilterParams = (jobListingArguments, filterValues) => {
  const params = new URLSearchParams()

  if (!jobListingArguments) return params

  // candidateTypes -> candidateTypeId (resolved IDs preferred)
  if (jobListingArguments.resolvedCandidateTypeIds?.length > 0) {
    params.set('candidateTypeId', jobListingArguments.resolvedCandidateTypeIds[0])
  } else if (jobListingArguments.candidateTypes?.length > 0) {
    const match = filterValues?.candidateTypes?.find(
      ct => ct.name?.toLowerCase() === jobListingArguments.candidateTypes[0]?.toLowerCase()
    )
    if (match) params.set('candidateTypeId', match.id || match.value)
  }

  // specialties -> specialtyIds (resolved IDs preferred)
  if (jobListingArguments.resolvedSpecialtyIds?.length > 0) {
    params.set('specialtyIds', jobListingArguments.resolvedSpecialtyIds.join('|'))
  } else if (jobListingArguments.specialties?.length > 0) {
    const ids = jobListingArguments.specialties
      .map(name => matchSpecialty(name, filterValues?.specialties))
      .filter(Boolean)
    if (ids.length > 0) params.set('specialtyIds', ids.join('|'))
  }

  // subspecialties -> subspecialtyIds (resolved IDs preferred)
  if (jobListingArguments.resolvedSubspecialtyIds?.length > 0) {
    params.set('subspecialtyIds', jobListingArguments.resolvedSubspecialtyIds.join('|'))
  } else if (jobListingArguments.subspecialties?.length > 0) {
    const ids = jobListingArguments.subspecialties
      .map(name => matchSpecialty(name, filterValues?.subspecialties))
      .filter(Boolean)
    if (ids.length > 0) params.set('subspecialtyIds', ids.join('|'))
  }

  // locations -> states (resolved abbreviations preferred)
  if (jobListingArguments.resolvedStateAbbreviations?.length > 0) {
    params.set('states', jobListingArguments.resolvedStateAbbreviations.join('|'))
  } else if (jobListingArguments.locations?.length > 0) {
    const stateValues = new Set()
    for (const loc of jobListingArguments.locations) {
      const candidates = []
      if (loc.state) candidates.push(loc.state)
      if (loc.location) candidates.push(...parseStateFromLocation(loc.location))

      for (const candidate of candidates) {
        const matched = matchState(candidate, filterValues?.states)
        if (matched) {
          stateValues.add(matched)
          break
        }
      }
    }
    if (stateValues.size > 0) params.set('states', [...stateValues].join('|'))
  }

  // practiceTypes -> practiceTypes (resolved IDs preferred)
  if (jobListingArguments.resolvedPracticeTypeIds?.length > 0) {
    params.set('practiceTypes', jobListingArguments.resolvedPracticeTypeIds.join('|'))
  } else if (jobListingArguments.practiceTypes?.length > 0) {
    const ids = jobListingArguments.practiceTypes
      .map(name => {
        const match = filterValues?.practiceTypes?.find(
          pt => pt.name?.toLowerCase() === name?.toLowerCase()
        )
        return match?.id || match?.value
      })
      .filter(Boolean)
    if (ids.length > 0) params.set('practiceTypes', ids.join('|'))
  }

  // salaryMinimum -> salaryMin
  if (jobListingArguments.salaryMinimum) {
    params.set('salaryMin', jobListingArguments.salaryMinimum.toString())
  }

  // pslfCompatible -> pslfCompatible
  if (jobListingArguments.pslfCompatible) {
    params.set('pslfCompatible', 'true')
  }

  // loanForgiveness -> loanForgiveness
  if (jobListingArguments.loanForgiveness) {
    params.set('loanForgiveness', 'true')
  }

  // academicTypes -> academicTypes (resolved IDs preferred)
  if (jobListingArguments.resolvedAcademicTypeIds?.length > 0) {
    params.set('academicTypes', jobListingArguments.resolvedAcademicTypeIds.join('|'))
  } else if (jobListingArguments.academicTypes?.length > 0) {
    const ids = jobListingArguments.academicTypes
      .map(name => {
        const match = filterValues?.academicTypes?.find(
          at => at.name?.toLowerCase() === name?.toLowerCase()
        )
        return match?.id || match?.value
      })
      .filter(Boolean)
    if (ids.length > 0) params.set('academicTypes', ids.join('|'))
  }

  // areaTypes -> areaTypes (resolved IDs preferred)
  if (jobListingArguments.resolvedAreaTypeIds?.length > 0) {
    params.set('areaTypes', jobListingArguments.resolvedAreaTypeIds.join('|'))
  } else if (jobListingArguments.areaTypes?.length > 0) {
    const ids = jobListingArguments.areaTypes
      .map(name => {
        const match = filterValues?.areaTypes?.find(
          at => at.name?.toLowerCase() === name?.toLowerCase()
        )
        return match?.id || match?.value
      })
      .filter(Boolean)
    if (ids.length > 0) params.set('areaTypes', ids.join('|'))
  }

  // visas -> visaIds (resolved IDs preferred)
  if (jobListingArguments.resolvedVisaIds?.length > 0) {
    params.set('visaIds', jobListingArguments.resolvedVisaIds.join('|'))
  } else if (jobListingArguments.visas?.length > 0) {
    const ids = jobListingArguments.visas
      .map(name => {
        const match = filterValues?.visas?.find(
          v => v.name?.toLowerCase() === name?.toLowerCase()
        )
        return match?.id || match?.value
      })
      .filter(Boolean)
    if (ids.length > 0) params.set('visaIds', ids.join('|'))
  }

  return params
}

/**
 * Reverse-maps URL filter params back to a jobListingArguments-shaped object.
 * This is the inverse of mapJobListingArgumentsToFilterParams.
 *
 * @param {URLSearchParams} searchParams - Current URL search params
 * @param {Object} filterValues - Available filter values from Redux (for name lookups)
 * @returns {Object} jobListingArguments-shaped object
 */
export const mapFilterParamsToJobListingArguments = (searchParams, filterValues) => {
  const args = {}

  const lookupNames = (idsString, valuesKey) => {
    if (!idsString) return null
    const ids = idsString.split('|')
    const values = filterValues?.[valuesKey]
    if (!values || !Array.isArray(values)) return ids
    return ids.map(id => {
      const match = values.find(v => String(v.id || v.value) === String(id))
      return match?.name || id
    })
  }

  const specialtyIds = searchParams.get('specialtyIds')
  if (specialtyIds) {
    args.specialties = lookupNames(specialtyIds, 'specialties')
  }

  const subspecialtyIds = searchParams.get('subspecialtyIds')
  if (subspecialtyIds) {
    args.subspecialties = lookupNames(subspecialtyIds, 'subspecialties')
  }

  const states = searchParams.get('states')
  if (states) {
    const names = lookupNames(states, 'states')
    args.locations = names.map(name => ({ location: name }))
  }

  const salaryMin = searchParams.get('salaryMin')
  if (salaryMin) {
    args.salaryMinimum = Number(salaryMin)
  }

  const practiceTypes = searchParams.get('practiceTypes')
  if (practiceTypes) {
    args.practiceTypes = lookupNames(practiceTypes, 'practiceTypes')
  }

  const academicTypes = searchParams.get('academicTypes')
  if (academicTypes) {
    args.academicTypes = lookupNames(academicTypes, 'academicTypes')
  }

  const areaTypes = searchParams.get('areaTypes')
  if (areaTypes) {
    args.areaTypes = lookupNames(areaTypes, 'areaTypes')
  }

  const visaIds = searchParams.get('visaIds')
  if (visaIds) {
    args.visas = lookupNames(visaIds, 'visas')
  }

  if (searchParams.get('pslfCompatible') === 'true') {
    args.pslfCompatible = true
  }

  if (searchParams.get('loanForgiveness') === 'true') {
    args.loanForgiveness = true
  }

  return args
}

/**
 * Builds a human-readable message describing filter changes.
 *
 * @param {URLSearchParams} previousParams
 * @param {URLSearchParams} currentParams
 * @param {Object} filterValues - available filter values for label lookup
 * @returns {string|null} message or null if no meaningful change
 */
export const buildFilterChangeMessage = (previousParams, currentParams, filterValues) => {
  const changes = []

  const filterKeys = filterDefinitions
    .filter(fd => fd.queryStringKey && fd.isSearchParamVisible)
    .map(fd => ({ key: fd.queryStringKey, name: fd.displayName, valuesKey: fd.valuesResponseKey }))

  for (const { key, name, valuesKey } of filterKeys) {
    const prev = previousParams.get(key) || ''
    const curr = currentParams.get(key) || ''

    if (prev === curr) continue

    if (curr && !prev) {
      const labels = resolveFilterLabels(curr, valuesKey, filterValues)
      changes.push(`Added ${name}: ${labels}`)
    } else if (!curr && prev) {
      changes.push(`Removed ${name}`)
    } else {
      const labels = resolveFilterLabels(curr, valuesKey, filterValues)
      changes.push(`Changed ${name} to: ${labels}`)
    }
  }

  if (changes.length === 0) return null
  return changes.join('. ') + '.'
}

const resolveFilterLabels = (valueString, valuesKey, filterValues) => {
  if (!filterValues || !valuesKey) return valueString

  const values = filterValues[valuesKey]
  if (!values || !Array.isArray(values)) return valueString

  const ids = valueString.split('|')
  const labels = ids.map(id => {
    const match = values.find(v => String(v.id || v.value) === String(id))
    return match?.name || id
  })
  return labels.join(', ')
}

/**
 * Builds a human-readable summary of all currently active filters.
 *
 * @param {URLSearchParams} searchParams
 * @param {Object} filterValues - available filter values for label lookup
 * @returns {string|null} summary or null if no filters are active
 */
export const buildActiveFiltersSummary = (searchParams, filterValues) => {
  const parts = []

  const filterKeys = filterDefinitions
    .filter(fd => fd.queryStringKey && fd.isSearchParamVisible)
    .map(fd => ({ key: fd.queryStringKey, name: fd.displayName, valuesKey: fd.valuesResponseKey }))

  for (const { key, name, valuesKey } of filterKeys) {
    const value = searchParams.get(key)
    if (!value) continue
    const labels = resolveFilterLabels(value, valuesKey, filterValues)
    parts.push(`${name}: ${labels}`)
  }

  if (parts.length === 0) return null
  return parts.join('. ') + '.'
}

/**
 * Wraps traditional job objects in the rankedJob format for unified rendering.
 *
 * @param {Array} jobs - Traditional job list
 * @returns {Array} rankedJob-shaped array
 */
export const wrapTraditionalJobsAsRanked = (jobs) => {
  if (!jobs) return []
  return jobs.map(job => ({
    job,
    matchTier: null,
    justification: null,
    chatbotMessageId: null,
  }))
}
